import api from "../../utils/api";

// 퍼스트아메리칸(FA) 권원보험 검수 — NZSC-3189.
// api-admin /api/v1/firstam/applications(읽기) + /approve-1st|hold|reapprove|reject|cancel|withdraw|memos(write).
// NZSC-3900: v2(rfi_flow='v2') 는 2차 심사 폐지 — hold/reapprove/change-judge/recheck-*/registry-recheck/memos.
// legacy(rfi_flow='legacy', 테스터 오픈 듀얼 패스) 는 origin/dev 옛 화면이 그대로 쓰는 approve(2차 승인)/escalate/
// reanalysis/reanalyze/submit-rfi 를 아래 '레거시' 절에 원문 그대로 둔다. rfi-cutoff 는 legacy 2차 대기 탭 분류에 사용한다.
// 응답 봉투는 { code, message, data } (ResponseResourceDto) — 기존 insuranceStore 컨벤션 동일.
// 액션 actor 는 query param user_no 로 전달(없으면 BE 가 'ADMIN' 기본). admin 은 user_no 미보유 →
//   감사 추적용으로 localStorage.name(관리자명)을 넘긴다.

export const state = {
  _items: [],
  _total: 0,
  // FA연동로그(firstam_api_log) — admin-next monitor 와 동일 BE(/firstam/logs) 소비.
  _logs: [],
  _logTotal: 0,
  _logPage: 1,
  _logSize: 50,
};

export const mutations = {
  setFirstamList(state, { items, total }) {
    state._items = items || [];
    state._total = total || 0;
  },
  setFirstamLogs(state, { items, total, page, size }) {
    state._logs = items || [];
    state._logTotal = total || 0;
    state._logPage = page || 1;
    state._logSize = size || 50;
  },
};

const fnActor = () => localStorage.name || undefined;
const fnApplicationsUrl = (reviewVersion, suffix = "") =>
  `/api/v1/firstam${reviewVersion === "v2" ? "/v2" : ""}/applications${suffix}`;

// NZSC-3900 write 공통: {ok, data, message, status}. status 404 는 BE 미배포(준비 중)로 화면이 안내한다.
// timedOut: 클라이언트 대기 시간 초과(axios ECONNABORTED). 서버는 계속 처리 중일 수 있으므로 화면은 실패로 단정하지 말고
// 상세를 다시 읽어 실제 상태를 보여준다(NZSC-4033 — 등기부 재확인은 cold 재발급을 동기로 기다린다).
async function fnWrite(method, url, body, options = {}) {
  try {
    const res = await api[method](url, body, { params: { user_no: fnActor() }, ...options });
    return { ok: res.data?.code === 0, data: res.data?.data, message: res.data?.message };
  } catch (error) {
    return {
      ok: false,
      status: error?.response?.status || error?.status,
      message: error?.response?.data?.message || error?.data?.message,
      timedOut: error?.code === "ECONNABORTED",
    };
  }
}

export const actions = {
  async acFirstamApplicationsAll({ commit }, { currentStat, reviewVersion } = {}) {
    const url = fnApplicationsUrl(reviewVersion);
    const size = 200;
    try {
      const first = await api.get(url, { params: { page: 1, size, currentStat } });
      if (!(first.data?.code === 0 && first.data.data)) return false;
      const items = [...(first.data.data.items || [])];
      const total = first.data.data.total || 0;
      const totalPages = Math.max(1, Math.ceil(total / size));
      for (let page = 2; page <= totalPages; page += 1) {
        const res = await api.get(url, { params: { page, size, currentStat } });
        const next = res.data?.code === 0 ? res.data.data?.items : null;
        if (!next || !next.length) break; // 순회 중 행 감소로 빈 페이지면 종료.
        items.push(...next);
      }
      commit("setFirstamList", { items, total });
      return true;
    } catch (error) {
      console.warn("acFirstamApplicationsAll error");
      return false;
    }
  },
  // 청약의뢰 배치 잔금일 컷오프(다음 영업일). REVIEW_2ND 를 1차검토완료/2차검토예정으로 가르는 기준.
  // api-public HolidayService(공공데이터 특일정보) 단일 소스 — FE 영업일 재계산 금지. 실패 시 null 반환.
  async acFirstamRfiCutoff() {
    try {
      const res = await api.get("/api/v1/firstam/rfi-cutoff");
      if (res.data?.code === 0 && res.data.data) return res.data.data.cutoff || null;
      return null;
    } catch {
      // Axios 오류 객체에는 인증 헤더가 포함될 수 있어 출력하지 않는다.
      console.warn("acFirstamRfiCutoff unavailable");
      return null;
    }
  },
  // FA연동로그 목록(firstam_api_log). 필터: endpoint/errorKind/q(rtn_msg LIKE)/기간(from,to YYYY-MM-DD).
  async acFirstamLogs({ commit }, { page, size, endpoint, errorKind, q, from, to } = {}) {
    const url = "/api/v1/firstam/logs";
    try {
      const res = await api.get(url, {
        params: { page, size, endpoint, errorKind, q, from, to },
      });
      if (res.data?.code === 0 && res.data.data) {
        commit("setFirstamLogs", {
          items: res.data.data.items,
          total: res.data.data.total,
          page: res.data.data.page,
          size: res.data.data.size,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.warn("acFirstamLogs error");
      return false;
    }
  },
  // FA연동로그 상세 — raw 요청/응답 전체. data 그대로 반환(없으면 null).
  async acFirstamLogDetail(_, { seq }) {
    const url = `/api/v1/firstam/logs/${seq}`;
    try {
      const res = await api.get(url);
      if (res.data?.code === 0 && res.data.data) return res.data.data;
      return null;
    } catch (error) {
      console.warn("acFirstamLogDetail error");
      return null;
    }
  },
  // 청약 상세 + 이벤트 타임라인. data 그대로 반환(없으면 null).
  async acFirstamDetail(_, { seq, reviewVersion }) {
    const url = fnApplicationsUrl(reviewVersion, `/${seq}`);
    try {
      const res = await api.get(url);
      if (res.data?.code === 0 && res.data.data) return res.data.data;
      return null;
    } catch (error) {
      console.warn("acFirstamDetail error");
      return null;
    }
  },
  // 청약(rfi) 전 계약정보 수정 — 잔금일·계약일·주소·매매금액·고유번호·유형. 잔금일 변경 시 cow 동기화는 BE.
  async acFirstamUpdateInfo(_, { seq, patch, reviewVersion }) {
    const url = fnApplicationsUrl(reviewVersion, `/${seq}/info`);
    try {
      const res = await api.patch(url, patch, {
        params: { user_no: fnActor() },
      });
      return { ok: res.data?.code === 0, data: res.data?.data };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // 토지등기부 발급(검수용) — cold가 토지 고유번호 도출 + Codef 발급/파싱/저장. {landUniqueNo, gapgu, eulgu, ...} 반환.
  async acFirstamLandRegistry(_, { seq, reviewVersion }) {
    const url = fnApplicationsUrl(reviewVersion, `/${seq}/land-registry`);
    try {
      const res = await api.post(url, undefined, {
        params: { user_no: fnActor() },
      });
      if (res.data?.code === 0 && res.data.data) {
        return { ok: true, data: res.data.data };
      }
      return { ok: false, message: res.data?.message };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // 고객 실시간 채팅 세션(NZSC-3189 #7/R9, loan-relay 재사용). {loanRelayHost, token, consultationId, ...} 반환.
  async acFirstamChatSession(_, { seq, reviewVersion }) {
    const url = fnApplicationsUrl(reviewVersion, `/${seq}/chat-session`);
    try {
      const res = await api.get(url);
      if (res.data?.code === 0 && res.data.data) return res.data.data;
      return null;
    } catch (error) {
      console.warn("acFirstamChatSession error");
      return null;
    }
  },
  // 1차 수동 승인: 리포트 승인이 완료된 REVIEW_1ST → first-pass 후속 처리.
  async acFirstamApprove1st(_, { seq, reviewVersion }) {
    const url = fnApplicationsUrl(reviewVersion, `/${seq}/approve-1st`);
    try {
      const res = await api.patch(url, undefined, {
        params: { user_no: fnActor() },
      });
      return {
        ok: res.data?.code === 0,
        data: res.data?.data,
        message: res.data?.message,
      };
    } catch (error) {
      return {
        ok: false,
        message: error?.response?.data?.message || error?.data?.message,
      };
    }
  },
  // 거절(1차/2차 검토중) → REJECTED.
  async acFirstamReject(_, { seq, reason, reviewVersion }) {
    const url = fnApplicationsUrl(reviewVersion, `/${seq}/reject`);
    try {
      const res = await api.patch(url, { reason }, { params: { user_no: fnActor() } });
      return { ok: res.data?.code === 0 };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // 취소(검토 단계) → CANCELLED.
  async acFirstamCancel(_, { seq, reason, reviewVersion }) {
    const url = fnApplicationsUrl(reviewVersion, `/${seq}/cancel`);
    try {
      const res = await api.patch(url, { reason }, { params: { user_no: fnActor() } });
      return { ok: res.data?.code === 0 };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // 증권 철회/청약취소: FA rfi/cancel(api-public 동기 경유). 발급=철회 / 청약·미발급=취소.
  // NZSC-3900 WI-7: refund_type(RIGHTS_CHANGE_CANCEL·USER_CANCEL_AFTER_RFI·INFO_CHANGE_CANCEL·WITHDRAW_AFTER_POLICY)
  // 로 환불 유형을 지정. 미지정 시 BE 가 상태로 기본 유형을 정한다.
  async acFirstamWithdraw(_, { seq, reason, refundType, reviewVersion }) {
    const url = fnApplicationsUrl(reviewVersion, `/${seq}/withdraw`);
    try {
      const body = { reason };
      if (refundType) body.refund_type = refundType;
      const res = await api.post(url, body, { params: { user_no: fnActor() } });
      return { ok: res.data?.code === 0, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // ── NZSC-3900 청약 시점 변경 ─────────────────────────────────────────────
  // 모니터링 필요 전환: REVIEW_1ST → HOLD (노출·구독 활성, 청약 보류, 심사 보류 알림톡). holdReason.kind 필수.
  async acFirstamHold(_, { seq, holdReason }) {
    return fnWrite("patch", `/api/v1/firstam/v2/applications/${seq}/hold`, { holdReason });
  },
  // 재심사 승인(말소 확인 후): HOLD → APPROVED + 즉시 청약.
  async acFirstamReapprove(_, { seq }) {
    return fnWrite("patch", `/api/v1/firstam/v2/applications/${seq}/reapprove`, undefined);
  },
  // 변동 검수 판정: 문제 없음(OK) / 문제 있음(ISSUE → FA 확인 중). WI-5 BE.
  async acFirstamChangeJudge(_, { seq, result, reason }) {
    return fnWrite("patch", `/api/v1/firstam/v2/applications/${seq}/change-judge`, { result, reason });
  },
  // 재심사(정보변경) 승인: 변경 적용 + 청약 완료 건이면 보험사 전달. WI-6 BE.
  async acFirstamRecheckApprove(_, { seq }) {
    return fnWrite("patch", `/api/v1/firstam/v2/applications/${seq}/recheck-approve`, undefined);
  },
  // 재심사(정보변경) 부적합. WI-6 BE.
  async acFirstamRecheckUnfit(_, { seq, reason }) {
    return fnWrite("patch", `/api/v1/firstam/v2/applications/${seq}/recheck-unfit`, { reason });
  },
  // 등기부 재확인(모니터링 필요 건 전용): 등기부 즉시 재발급 → 보류 사유 등기 잔존/해소 갱신. WI-4 BE.
  // 동기 재발급(등기소 10~60초+). api-admin→api-public 120초보다 길게 잡아 중간 타임아웃이 먼저 끊지 않게 한다.
  async acFirstamRegistryRecheck(_, { seq }) {
    return fnWrite("post", `/api/v1/firstam/v2/applications/${seq}/registry-recheck`, {}, { timeout: 150000 });
  },
  // 검수 메모 목록(오래된 순). [{seq, memo, adminId, createDate}]
  async acFirstamMemos(_, { seq }) {
    const url = `/api/v1/firstam/v2/applications/${seq}/memos`;
    try {
      const res = await api.get(url);
      if (res.data?.code === 0 && Array.isArray(res.data.data)) return res.data.data;
      return [];
    } catch (error) {
      console.warn("acFirstamMemos error");
      return [];
    }
  },
  // 검수 메모 추가 — 통화 내용·처리 사유. 사유가 필요한 액션은 화면에서 같은 사유를 메모로도 남긴다.
  async acFirstamAddMemo(_, { seq, memo }) {
    return fnWrite("post", `/api/v1/firstam/v2/applications/${seq}/memos`, { memo });
  },
  // ── 레거시(rfi_flow='legacy') 전용 — origin/dev 원문 그대로. ReviewDetailLegacyView 만 호출한다. ──
  // 2차 승인: REVIEW_2ND → APPROVED.
  async acFirstamApprove(_, { seq }) {
    const url = `/api/v1/firstam/applications/${seq}/approve`;
    try {
      const res = await api.patch(url, undefined, {
        params: { user_no: fnActor() },
      });
      return { ok: res.data?.code === 0 };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // FA케이스전달: REVIEW_2ND → FA_ESCALATED.
  async acFirstamEscalate(_, { seq, reason }) {
    const url = `/api/v1/firstam/applications/${seq}/escalate`;
    try {
      const res = await api.patch(url, { reason }, { params: { user_no: fnActor() } });
      return { ok: res.data?.code === 0 };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // 2차검토 cold 재분석 이력(스냅샷). report_seq 기준 최신순.
  async acFirstamReanalysisHistory(_, { seq }) {
    const url = `/api/v1/firstam/applications/${seq}/reanalysis`;
    try {
      const res = await api.get(url);
      return res.data?.code === 0 ? res.data.data : null;
    } catch (error) {
      return null;
    }
  },
  // 2차검토 cold 재분석 수동 트리거(검수자). cold skipDbWrites — 고객 report 무손상.
  async acFirstamReanalyzeManual(_, { seq }) {
    const url = `/api/v1/firstam/applications/${seq}/reanalyze`;
    try {
      const res = await api.post(url, {}, { params: { user_no: fnActor() } });
      return { ok: res.data?.code === 0, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
  // 단건 수동 청약의뢰(검수자). APPROVED(2차승인·미청약)만. 성공 시 RFI_REQUESTED 전이(19:30 배치 자동 스킵).
  async acFirstamSubmitRfi(_, { seq }) {
    const url = `/api/v1/firstam/applications/${seq}/submit-rfi`;
    try {
      const res = await api.post(url, {}, { params: { user_no: fnActor() } });
      return { ok: res.data?.code === 0, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return { ok: false, message: error?.data?.message };
    }
  },
};

export const getters = {};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
